/**
 * © 2025 Little Shilling, Inc.
 * Shon Little
 * Created: 2025-01-23
 */

import React, { useState, useRef, useCallback } from 'react';
import {
  Box,
  Button,
  Typography,
  Avatar,
  IconButton,
  Alert,
  Paper,
} from '@mui/material';
import {
  CloudUpload as UploadIcon,
  Delete as DeleteIcon,
  PhotoCamera as CameraIcon,
} from '@mui/icons-material';

import type { Area } from 'react-easy-crop/types';
import ImageCropDialog from '../../common/ImageCropDialog';
import { LogoUploadProps } from '../../../types/studioForms';
import { validateLogoFile } from '../../../utils/studioValidation';
import {
  createCroppedImageFile,
  createImagePreviewUrl,
  getLogoOutputSize,
} from '../../../utils/imageCrop';

export const LogoUpload: React.FC<LogoUploadProps> = ({
  currentLogo,
  onFileSelect,
  error,
  disabled = false,
  preview,
}) => {
  const [dragOver, setDragOver] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [cropDialogOpen, setCropDialogOpen] = useState(false);
  const [cropImageUrl, setCropImageUrl] = useState<string | null>(null);
  const [isCropping, setIsCropping] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Handle file selection
  const handleFileSelect = useCallback(
    async (file: File | null) => {
      if (file) {
        const validationError = validateLogoFile(file);
        if (validationError) {
          onFileSelect(null);
          setSelectedFile(null);
          return;
        }
        setSelectedFile(file);

        try {
          // Create preview URL for cropping
          const imageUrl = await createImagePreviewUrl(file);
          setCropImageUrl(imageUrl);
          setCropDialogOpen(true);
        } catch (cropError) {
          console.error('Failed to create image preview:', cropError);
          // Fallback to direct file selection without cropping
          onFileSelect(file);
        }
      } else {
        setSelectedFile(null);
        onFileSelect(null);
      }
    },
    [onFileSelect]
  );

  // Handle file input change
  const handleFileInputChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0] || null;
    handleFileSelect(file);
    // Reset input value to allow selecting the same file again
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  };

  // Handle drag and drop
  const handleDragOver = (event: React.DragEvent) => {
    event.preventDefault();
    if (!disabled) {
      setDragOver(true);
    }
  };

  const handleDragLeave = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);
  };

  const handleDrop = (event: React.DragEvent) => {
    event.preventDefault();
    setDragOver(false);

    if (disabled) return;

    const { files } = event.dataTransfer;
    if (files.length > 0) {
      handleFileSelect(files[0]);
    }
  };

  // Handle remove logo
  const handleRemove = () => {
    handleFileSelect(null);
  };

  // Handle click to select file
  const handleClick = () => {
    if (!disabled && fileInputRef.current) {
      fileInputRef.current.click();
    }
  };

  // Determine what image to show
  const displayImage = preview || currentLogo || undefined;
  const hasImage = Boolean(displayImage);

  // Determine border color based on state
  const getBorderColor = () => {
    if (dragOver) return 'primary.main';
    if (error) return 'error.main';
    return 'divider';
  };

  /**
   * Handles crop confirmation.
   */
  const handleCropConfirm = useCallback(
    async (croppedAreaPixels: Area) => {
      if (!selectedFile || !cropImageUrl) return;

      setIsCropping(true);
      try {
        // Create the cropped image file
        const outputSize = getLogoOutputSize(300, 1); // Square logo
        const croppedFile = await createCroppedImageFile(
          cropImageUrl,
          croppedAreaPixels,
          `logo-${Date.now()}`,
          outputSize,
          'image/jpeg',
          0.9
        );

        // Close crop dialog
        setCropDialogOpen(false);
        setCropImageUrl(null);

        // Call the parent callback with the cropped file
        onFileSelect(croppedFile);
      } catch (cropError) {
        console.error('Failed to crop image:', cropError);
      } finally {
        setIsCropping(false);
      }
    },
    [selectedFile, cropImageUrl, onFileSelect]
  );

  /**
   * Handles crop cancellation.
   */
  const handleCropCancel = useCallback(() => {
    setCropDialogOpen(false);
    setCropImageUrl(null);
    setSelectedFile(null);
    if (fileInputRef.current) {
      fileInputRef.current.value = '';
    }
  }, []);

  // Determine background color based on state
  const getBackgroundColor = () => {
    if (dragOver) return 'action.hover';
    if (disabled) return 'action.disabledBackground';
    return 'background.paper';
  };

  return (
    <Box sx={{ width: '100%' }}>
      <Typography variant="subtitle2" gutterBottom>
        Studio Logo
      </Typography>

      {/* Upload Area */}
      <Paper
        elevation={dragOver ? 4 : 1}
        sx={{
          p: 3,
          border: dragOver ? 2 : 1,
          borderColor: getBorderColor(),
          borderStyle: dragOver ? 'solid' : 'dashed',
          backgroundColor: getBackgroundColor(),
          cursor: disabled ? 'not-allowed' : 'pointer',
          transition: 'all 0.2s ease-in-out',
          position: 'relative',
          textAlign: 'center',
        }}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
        onClick={handleClick}
      >
        {hasImage ? (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Avatar
              src={displayImage}
              sx={{
                width: 120,
                height: 120,
                border: 2,
                borderColor: 'divider',
              }}
            >
              <CameraIcon sx={{ fontSize: 40 }} />
            </Avatar>

            <Box sx={{ display: 'flex', gap: 1 }}>
              <Button
                variant="outlined"
                size="small"
                startIcon={<UploadIcon />}
                disabled={disabled}
                onClick={e => {
                  e.stopPropagation();
                  handleClick();
                }}
              >
                Change Logo
              </Button>

              <IconButton
                size="small"
                color="error"
                disabled={disabled}
                onClick={e => {
                  e.stopPropagation();
                  handleRemove();
                }}
                sx={{ ml: 1 }}
              >
                <DeleteIcon />
              </IconButton>
            </Box>

            <Typography variant="caption" color="text.secondary">
              Click to change or drag a new image here
            </Typography>

            {/* File Information */}
            {selectedFile && (
              <Box sx={{ mt: 1, textAlign: 'center' }}>
                <Typography variant="caption" color="text.secondary">
                  <strong>{selectedFile.name}</strong>
                </Typography>
                <Typography
                  variant="caption"
                  color="text.secondary"
                  display="block"
                >
                  {(selectedFile.size / 1024 / 1024).toFixed(2)} MB
                </Typography>
              </Box>
            )}
          </Box>
        ) : (
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              gap: 2,
            }}
          >
            <Avatar
              sx={{
                width: 120,
                height: 120,
                backgroundColor: 'action.hover',
                border: 2,
                borderColor: 'divider',
                borderStyle: 'dashed',
              }}
            >
              <UploadIcon sx={{ fontSize: 40, color: 'text.secondary' }} />
            </Avatar>

            <Box sx={{ textAlign: 'center' }}>
              <Typography variant="body1" gutterBottom>
                Upload Studio Logo
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Drag and drop an image here, or click to select
              </Typography>
              <Typography
                variant="caption"
                color="text.secondary"
                display="block"
              >
                Supports JPEG, PNG, GIF, WebP (max 5MB)
              </Typography>
            </Box>

            <Button
              variant="contained"
              startIcon={<UploadIcon />}
              disabled={disabled}
              onClick={e => {
                e.stopPropagation();
                handleClick();
              }}
            >
              Select Image
            </Button>
          </Box>
        )}

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/jpeg,image/png,image/gif,image/webp"
          onChange={handleFileInputChange}
          style={{ display: 'none' }}
          disabled={disabled}
        />
      </Paper>

      {/* Error message */}
      {error && (
        <Alert severity="error" sx={{ mt: 1 }}>
          {error}
        </Alert>
      )}

      {/* Help text */}
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ mt: 1, display: 'block' }}
      >
        Your logo will be displayed on your studio profile and in search
        results. For best results, use a square image with a transparent
        background.
      </Typography>

      {/* Image Crop Dialog */}
      {cropImageUrl && (
        <ImageCropDialog
          open={cropDialogOpen}
          imageUrl={cropImageUrl}
          title="Crop Studio Logo"
          showCircularCrop={false}
          aspect={1}
          onConfirm={handleCropConfirm}
          onCancel={handleCropCancel}
          loading={isCropping}
        />
      )}
    </Box>
  );
};

export default LogoUpload;
